import { Package } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import { variantLabel } from "@/lib/variant-label"
import type {
  ShipmentProduct,
  ShipmentVariantStatement,
} from "@/types/shipments"

/** How many pinned members are named before the row switches to "+N more". */
const PINNED_LABEL_CAP = 3

/**
 * OKS-987 — the product identity block: what the operator is packing.
 *
 * Rendered on collab queue rows and inside the mark-shipped dialog's header, so
 * the row and the confirmation state the same parcel the same way.
 *
 * # ⚠️ Two distinct photo-absence states, and the second is the forgettable one
 *
 * `photo_url === null` means the gift has no photo. A URL that is present but
 * FAILS to load (rehost rot, an expired signature, a 404) is a different state
 * and degrades to the SAME neutral tile — never the browser's broken-image
 * glyph, and never a different box, so there is no layout shift either way.
 * Mirrors the creator-side `ProductBlock` and `MissionCard`'s `coverErrored`.
 *
 * # The thumbnail is not interactive
 *
 * No link, no button. Same rule as OKS-985's plain-text reward title: a mis-tap
 * during a packing run must not navigate anywhere.
 *
 * `alt=""` because the product name is the adjacent text — announcing it twice
 * is noise to a screen-reader operator, and the fallback icon is `aria-hidden`
 * for the same reason.
 */
export function ShipmentProductBlock({
  product,
  compact = false,
}: {
  product: ShipmentProduct
  /** Dialog header variant: no variant statement (see `ShipmentRowContent`). */
  compact?: boolean
}) {
  const { t } = useTranslation()
  const [photoErrored, setPhotoErrored] = useState(false)
  const photoUrl = photoErrored ? null : product.photo_url

  return (
    <div className="flex items-start gap-2" data-testid="shipments-row-product">
      <div
        className={cn(
          "flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md",
          !photoUrl && "bg-muted text-muted-foreground",
        )}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            className="size-full object-cover"
            onError={() => setPhotoErrored(true)}
            data-testid="shipments-row-product-photo"
          />
        ) : (
          <Package className="size-4" aria-hidden="true" />
        )}
      </div>

      <div className="flex min-w-0 flex-col gap-1">
        {/* Wraps, never truncates — it is the identifying info, same rule the
            mission title and OKS-988's re-request reason follow. */}
        <span
          className="text-sm font-medium break-words"
          data-testid="shipments-row-product-name"
        >
          {product.name}
        </span>
        <span
          className="text-muted-foreground text-xs break-words"
          data-testid="shipments-row-product-meta"
        >
          {t("collabs.ship.product.qty", { count: product.quantity })}
          {!compact && (
            <>
              {" · "}
              <VariantStatement statement={product.variant} />
            </>
          )}
        </span>
      </div>
    </div>
  )
}

/**
 * The variant sentence (BRIEF §3.2).
 *
 * ⚠️ A SENTENCE, never a colour, badge or icon alone — a screen-reader operator
 * gets exactly what a sighted one gets. Same rule OKS-985 applied to the
 * `Reward` badge.
 *
 * ⚠️ `orphaned` is NOT folded into `open_choice`. The brand pinned a subset and
 * the catalogue lost it; saying "chosen at ship time" alone would silently
 * widen the mission from "this variant" back to "anything", which is the exact
 * failure `variant_pinned` exists as a separate column to prevent.
 *
 * ⚠️ Literal `t("…")` calls per the OKS-8 i18n health-check posture — the
 * checker's static analysis needs the key as a string literal, so this branches
 * rather than interpolating a key.
 */
function VariantStatement({
  statement,
}: {
  statement: ShipmentVariantStatement
}) {
  const { t } = useTranslation()

  switch (statement.mode) {
    case "none":
      return <>{t("collabs.ship.variant.statement.none")}</>

    case "orphaned":
      return <>{t("collabs.ship.variant.statement.orphaned")}</>

    case "fixed":
      return (
        <>
          {t("collabs.ship.variant.statement.fixed", {
            label: statement.variants[0]
              ? variantLabel(statement.variants[0], t)
              : t("collabs.ship.variant.unnamed"),
          })}
        </>
      )

    case "pinned_choice": {
      const named = statement.variants.slice(0, PINNED_LABEL_CAP)
      const remainder = statement.count - named.length
      // ⚠️ `sku: false` — this is the ONE multi-entry list on the row, and the
      // only place three delimiter types collide (` · `, `, `, ` — `). The
      // titles carry the choice; the SKU is pick-time detail and stays in the
      // dialog's `<option>`s and in single-variant statements (PO, 82).
      const labels = named
        .map((option) => variantLabel(option, t, { sku: false }))
        .join(", ")

      return (
        <>
          {t("collabs.ship.variant.statement.pinned_choice", { labels })}
          {remainder > 0 &&
            ` ${t("collabs.ship.variant.statement.more", { count: remainder })}`}
        </>
      )
    }

    case "open_choice":
      // The COUNT, not a list: `variants` is empty by design for this mode, so
      // a 40-variant product costs the row one number instead of 40 objects.
      return (
        <>
          {t("collabs.ship.variant.statement.open_choice", {
            count: statement.count,
          })}
        </>
      )
  }
}
